import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Box, Typography, Button,
  Switch, FormControlLabel, 
  TextField, Autocomplete,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useGetUserStandardPlanMutation, useCreateStandardPlanMutation } from 'services/user-service';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import config from '../../../config';
const PLAN_DETAILS = {
  LEADS: {
    MONTHLY: {
      STARTER: { 
        price: '$49', 
        priceId: config.LEADS_MONTHLY_STARTER_PRICE_ID
      },
      GROWTH: { 
        price: '$99', 
        priceId: config.LEADS_MONTHLY_GROWTH_PRICE_ID
      },
      INFINITY: { 
        price: '$199', 
        priceId: config.LEADS_MONTHLY_INFINITY_PRICE_ID
      }
    },
    YEARLY: {
      STARTER: { 
        price: '$49', 
        priceId: config.LEADS_YEARLY_STARTER_PRICE_ID
      },
      GROWTH: { 
        price: '$99', 
        priceId: config.LEADS_YEARLY_GROWTH_PRICE_ID
      },
      INFINITY: { 
        price: '$199', 
        priceId: config.LEADS_YEARLY_INFINITY_PRICE_ID
      }
    },
    FEATURES: {
      STARTER: [
        'Lead Database Access',
        'Basic Lead Scoring',
        'Export Capabilities'
      ],
      GROWTH: [
        'Advanced Lead Analytics',
        'Custom Lead Filters',
        'Team Collaboration'
      ],
      INFINITY: [
        'Unlimited Lead Exports',
        'AI-Powered Lead Scoring',
        'Priority Support'
      ]
    }
  },
  OUTREACH: {
    MONTHLY: {
      STARTER: { 
        price: '$39', 
        priceId: config.OUTREACH_MONTHLY_STARTER_PRICE_ID
      },
      GROWTH: { 
        price: '$99', 
        priceId: config.OUTREACH_MONTHLY_GROWTH_PRICE_ID
      },
      INFINITY: { 
        price: '$199', 
        priceId: config.OUTREACH_MONTHLY_INFINITY_PRICE_ID
      }
    },
    YEARLY: {
      STARTER: { 
        price: '$39', 
        priceId: config.OUTREACH_YEARLY_STARTER_PRICE_ID
      },
      GROWTH: { 
        price: '$99', 
        priceId: config.OUTREACH_YEARLY_GROWTH_PRICE_ID
      },
      INFINITY: { 
        price: '$199', 
        priceId: config.OUTREACH_YEARLY_INFINITY_PRICE_ID
      }
    },
    FEATURES: {
      STARTER: [
        'Basic Campaign Management',
        'Email Templates',
        'Basic Analytics'
      ],
      GROWTH: [
        'Advanced Campaign Tools',
        'A/B Testing',
        'Automation Workflows'
      ],
      INFINITY: [
        'Custom Integration',
        'Advanced Automation',
        'Dedicated Support'
      ]
    }
  }
};

const UserStandardPlan = ({ open, onClose, allWorkspaces, userId }) => {
  const navigate = useNavigate();
  const [getUserStandardPlan] = useGetUserStandardPlanMutation();
  const [createStandardPlan] = useCreateStandardPlanMutation();
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planType, setPlanType] = useState('LEADS');
  const [currentPlans, setCurrentPlans] = useState({
    LEADS: null,
    OUTREACH: null
  });

  const getPlansForType = (type, isYearly) => {
    const planPeriod = isYearly ? 'YEARLY' : 'MONTHLY';
    return [
      {
        id: 'STARTER',
        title: 'Starter',
        priceId: PLAN_DETAILS[type][planPeriod].STARTER.priceId
      },
      {
        id: 'GROWTH',
        title: 'Growth',
        priceId: PLAN_DETAILS[type][planPeriod].GROWTH.priceId
      },
      {
        id: 'INFINITY',
        title: 'Infinity',
        priceId: PLAN_DETAILS[type][planPeriod].INFINITY.priceId
      }
    ];
  };

  useEffect(() => {
    if (allWorkspaces?.length > 0) {
      const defaultWorkspace = allWorkspaces[0];
      setSelectedWorkspace(defaultWorkspace);

      const fetchCurrentPlan = async () => {
        try {
          const userPlan = await getUserStandardPlan(defaultWorkspace._id).unwrap();
          
          // Get both leads and outreach plans
          const leadsPlanId = userPlan?.subscription?.leads?.planId;
          const outreachPlanId = userPlan?.subscription?.sendingWarmup?.planId;

          // Set current plans for both types
          if (leadsPlanId) {
            const leadsPlans = getPlansForType('LEADS', false);
            const matchedLeadsPlan = leadsPlans.find(plan => plan.priceId === leadsPlanId);
            setCurrentPlans(prev => ({ ...prev, LEADS: matchedLeadsPlan }));
          }

          if (outreachPlanId) {
            const outreachPlans = getPlansForType('OUTREACH', false);
            const matchedOutreachPlan = outreachPlans.find(plan => plan.priceId === outreachPlanId);
            setCurrentPlans(prev => ({ ...prev, OUTREACH: matchedOutreachPlan }));
          }

          // Set initial plan type and selected plan
          const isOutreachPlan = outreachPlanId ? true : false;
          setPlanType(isOutreachPlan ? 'OUTREACH' : 'LEADS');
          
          const currentPlan = isOutreachPlan ? 
            getPlansForType('OUTREACH', false).find(plan => plan.priceId === outreachPlanId) :
            getPlansForType('LEADS', false).find(plan => plan.priceId === leadsPlanId);

          if (currentPlan) {
            setSelectedPlan(currentPlan);
          } else {
            const defaultPlan = getPlansForType(isOutreachPlan ? 'OUTREACH' : 'LEADS', false)[0];
            setSelectedPlan(defaultPlan);
          }
        } catch (error) {
          toast.error('Failed to fetch current plan');
          const defaultPlan = getPlansForType('LEADS', false)[0];
          setSelectedPlan(defaultPlan);
        } finally {
          setLoading(false);
        }
      };

      fetchCurrentPlan();
    } else {
      setLoading(false);
    }
  }, [allWorkspaces, getUserStandardPlan]);

  const handleFormSubmit = async (values, { setSubmitting }) => {
    if (!values.selectedPlan) {
      toast.error('Please select a plan');
      setSubmitting(false);
      return;
    }

    const workspaceId = selectedWorkspace?._id;
    const planId = values.selectedPlan.priceId;

    if (!planId) {
      toast.error('Invalid plan configuration');
      setSubmitting(false);
      return;
    }

    try {
      const { message } = await createStandardPlan({
        user: userId,
        selectedWorkspace: workspaceId,
        planId,
        isYearly: values.isYearly,
        planType: values.planType
      }).unwrap();

      // Update current plans state
      setCurrentPlans(prev => ({
        ...prev,
        [values.planType]: values.selectedPlan
      }));

      toast.success(message || 'Plan updated successfully');
      onClose();
      navigate(0);
    } catch (error) {
      toast.error(error.data.message || 'Failed to update plan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Plan</DialogTitle>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h5">
          Current {planType} Plan: <Typography component="span" variant="body2">
            {currentPlans[planType]?.title || 'Free Plan'}
          </Typography>
        </Typography>
      </Box>

      <Formik
        initialValues={{
          selectedWorkspace,
          selectedPlan,
          isYearly: false,
          planType: 'LEADS'
        }}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue, handleBlur, isSubmitting, touched, errors }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isYearly"
                        checked={values.isYearly}
                        onChange={(event) => {
                          setFieldValue('isYearly', event.target.checked);
                          const plans = getPlansForType(values.planType, event.target.checked);
                          setFieldValue('selectedPlan', plans[0]);
                          setSelectedPlan(plans[0]);
                        }}
                        onBlur={handleBlur}
                      />
                    }
                    label="Yearly Plan"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    options={allWorkspaces}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Workspace"
                        variant="outlined"
                        onBlur={handleBlur}
                        error={touched.selectedWorkspace && Boolean(errors.selectedWorkspace)}
                        helperText={touched.selectedWorkspace && errors.selectedWorkspace}
                      />
                    )}
                    onChange={(event, newValue) => {
                      setSelectedWorkspace(newValue);
                      setFieldValue('selectedWorkspace', newValue);
                    }}
                    value={selectedWorkspace}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    options={getPlansForType(values.planType, values.isYearly)}
                    getOptionLabel={(option) => option.title || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Plan"
                        variant="outlined"
                        onBlur={handleBlur}
                        error={touched.selectedPlan && Boolean(errors.selectedPlan)}
                        helperText={touched.selectedPlan && errors.selectedPlan}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Typography variant="subtitle1">{option.title}</Typography>
                      </li>
                    )}
                    onChange={(event, newValue) => {
                      setSelectedPlan(newValue);
                      setFieldValue('selectedPlan', newValue);
                    }}
                    value={selectedPlan}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                Update Plan
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UserStandardPlan;